import { App, Select } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { type FC, useEffect, useMemo, useState } from 'react';
import type { GetOrderByIdWithDriversWarehousesQuery } from '@/generated/graphql/graphql';
import { useGetDistrictScheduleAndWorkload } from '../query/orders.hooks';
import { getScheduleSlotsForWeekday, parseDistrictSchedule } from '../utils/districtScheduleUtils';

dayjs.extend(utc);

export const time_ranges: Array<[string, string]> = [
  ['08:30', '10:00'],
  ['09:00', '11:00'],
  ['10:00', '12:00'],
  ['11:00', '13:00'],
  ['12:00', '14:00'],
  ['13:00', '15:00'],
  ['14:00', '16:00'],
  ['15:00', '17:00'],
  ['16:00', '18:00'],
];

interface WorkloadSelectProps {
  orderData: GetOrderByIdWithDriversWarehousesQuery;
  handleFieldChange: (path: string[], value: string | number) => void;
  isDisabled?: boolean;
}

interface Workload {
  workload: number;
  workloadSum: number;
  loadFactor: number;
  range: string[];
}

export const WorkloadSelect: FC<WorkloadSelectProps> = ({
  orderData,
  handleFieldChange,
  isDisabled = false,
}) => {
  const { message: messageApi } = App.useApp();
  const [workloads, setWorkloads] = useState<Workload[]>([]);

  const selectedDate = dayjs(orderData?.order_by_pk?.plannedDate).utc().format('YYYY-MM-DD');
  const selectedWeekday = orderData?.order_by_pk?.plannedDate
    ? dayjs(orderData.order_by_pk.plannedDate).utc().isoWeekday()
    : dayjs().isoWeekday();

  const districtId = orderData?.order_by_pk?.client?.districtId ?? '';
  const plannedDateTs = dayjs.utc(selectedDate, 'YYYY-MM-DD').startOf('day').toISOString();
  const nextDayTs = dayjs
    .utc(selectedDate, 'YYYY-MM-DD')
    .add(1, 'day')
    .startOf('day')
    .toISOString();
  const {
    data: districtData,
    isLoading: isWorkloadLoading,
    isError,
  } = useGetDistrictScheduleAndWorkload({
    districtId,
    plannedDate: plannedDateTs,
    nextDay: nextDayTs,
  });
  const districtSchedule = useMemo(() => parseDistrictSchedule(districtData), [districtData]);
  const slotsToShow = useMemo(
    () => getScheduleSlotsForWeekday(districtSchedule, selectedWeekday, time_ranges),
    [districtSchedule, selectedWeekday]
  );

  const districtWorkloadKey =
    districtData?.district_workload?.map((dw) => `${dw.timeperiod}-${dw.workload}`).join('|') ?? '';

  useEffect(() => {
    if (!districtData || isWorkloadLoading) {
      return;
    }

    if (isError) {
      messageApi.error('Cannot get district workloads');
      return;
    }

    if (!districtWorkloadKey && (districtData?.district_workload?.length ?? 0) > 0) {
      return;
    }

    // timeperiod is timestamptz (UTC); we use UTC hour/minute to match slot ranges
    const districtWorkloadsWithTime =
      districtData?.district_workload?.map((dw) => {
        const parsed = dayjs.utc(dw.timeperiod);
        return {
          ...dw,
          hour: parsed.hour(),
          minute: parsed.minute(),
        };
      }) ?? [];

    const updatedWorkloads = slotsToShow.map((range) => {
      const [startH, startM] = range[0].split(':').map((x) => parseInt(x, 10));
      const [endH, endM] = range[1].split(':').map((x) => parseInt(x, 10));
      const slotStartMinutes = startH * 60 + (Number.isNaN(startM) ? 0 : startM);
      const slotEndMinutes = endH * 60 + (Number.isNaN(endM) ? 0 : endM);
      const slotDurationHours = (slotEndMinutes - slotStartMinutes) / 60;

      const matchingRows = districtWorkloadsWithTime.filter(
        (w) =>
          w.hour * 60 + (Number.isNaN(w.minute) ? 0 : w.minute) >= slotStartMinutes &&
          w.hour * 60 + (Number.isNaN(w.minute) ? 0 : w.minute) < slotEndMinutes
      );

      const workloadSum = matchingRows
        .map((w) => w.workload)
        .reduce((sum, current) => sum + current, 0);
      const workloadAvg = slotDurationHours > 0 ? workloadSum / slotDurationHours : 0;

      const lf = districtData.district_by_pk?.loadFactor ?? 1;
      return {
        workload: workloadAvg * lf,
        workloadSum: workloadSum * lf,
        loadFactor: lf,
        range,
      };
    });

    setWorkloads(updatedWorkloads);
  }, [districtData, districtWorkloadKey, isError, isWorkloadLoading, messageApi, slotsToShow]);

  const getSelectedTimeRange = () => {
    if (orderData?.order_by_pk?.plannedDate && orderData?.order_by_pk?.plannedDateDuration) {
      const start = dayjs(orderData.order_by_pk.plannedDate).utc().format('HH:mm');
      const end = dayjs(orderData.order_by_pk.plannedDate)
        .utc()
        .add(orderData.order_by_pk.plannedDateDuration, 'minutes')
        .format('HH:mm');
      return `${start} - ${end}`;
    }
    return undefined;
  };
  const selectedTimeRangeValue = getSelectedTimeRange();
  const effectiveWorkloads =
    workloads.length === slotsToShow.length
      ? workloads
      : slotsToShow.map((range) => ({
          workload: 0,
          workloadSum: 0,
          loadFactor: 1,
          range,
        }));
  const handleTimeRangeChange = (value: string) => {
    if (typeof value !== 'string' || !value.includes(',')) {
      console.error('Invalid value format for time range:', value);
      return;
    }
    const [start, end] = value.split(',');
    const slotIndex = slotsToShow.findIndex((r) => r[0] === start && r[1] === end);
    if (
      slotIndex >= 0 &&
      (effectiveWorkloads[slotIndex]?.workload ?? 0) >=
        (effectiveWorkloads[slotIndex]?.loadFactor ?? 1)
    ) {
      return;
    }
    const baseDate = dayjs
      .utc(dayjs(orderData?.order_by_pk?.plannedDate).utc().format('YYYY-MM-DD'), 'YYYY-MM-DD')
      .startOf('day');

    const selectedTime = baseDate
      .set('hour', parseInt(start.split(':')[0], 10))
      .set('minute', parseInt(start.split(':')[1], 10))
      .set('second', 0)
      .millisecond(0);

    const duration = dayjs.duration(dayjs(end, 'HH:mm').diff(dayjs(start, 'HH:mm'))).asMinutes();

    handleFieldChange(['order_by_pk', 'plannedDate'], selectedTime.toISOString());
    handleFieldChange(['order_by_pk', 'plannedDateDuration'], duration);
  };

  const isSelectedInSlots =
    selectedTimeRangeValue &&
    slotsToShow.some((r) => `${r[0]} - ${r[1]}` === selectedTimeRangeValue);
  const selectValue = selectedTimeRangeValue
    ? selectedTimeRangeValue.replace(' - ', ',')
    : undefined;
  const isCustomTime = selectedTimeRangeValue != null && !isSelectedInSlots;

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <Select
        placeholder="Choose time"
        value={selectValue}
        optionLabelProp="label"
        onChange={handleTimeRangeChange}
        loading={isWorkloadLoading}
        disabled={isDisabled}
        style={{ width: '100%' }}
      >
        {isCustomTime && selectedTimeRangeValue && (
          <Select.Option key="custom" value={selectValue} label={selectedTimeRangeValue}>
            {selectedTimeRangeValue}
            <span style={{ marginLeft: 6, color: '#888', fontSize: 12 }}>(current)</span>
          </Select.Option>
        )}
        {slotsToShow.map((time, index) => {
          const rangeWorkload = effectiveWorkloads[index];
          const isSlotOccupied = (rangeWorkload.workload ?? 0) >= (rangeWorkload.loadFactor ?? 1);
          const isCurrentSelection = selectValue === `${time[0]},${time[1]}`;

          return (
            <Select.Option
              key={`${time[0]}-${time[1]}`}
              value={`${time[0]},${time[1]}`}
              label={`${time[0]} - ${time[1]}`}
              disabled={isSlotOccupied && !isCurrentSelection}
            >
              <span>
                {`${time[0]} - ${time[1]}`}
                {isCurrentSelection && (
                  <span style={{ marginLeft: 6, color: '#1890ff', fontWeight: 500 }}>
                    (selected)
                  </span>
                )}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', height: 30, width: 80 }}>
                {Array.from({ length: rangeWorkload.loadFactor }, (_, idx) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: rendering N identical workload markers
                    key={`${time[0]}-${time[1]}-${idx}`}
                    style={{
                      flex: rangeWorkload.workload > 2 ? 1 : 'none',
                      height: 15,
                      width: 15,
                      flexShrink: 1,
                      backgroundColor:
                        rangeWorkload.workload > idx ? `rgba(24, 144, 255, 1)` : 'lightgrey',
                      borderRadius: 2,
                      marginLeft: 3,
                      boxShadow: `0 0 0 1px ${rangeWorkload.workload > idx ? '#1890ff' : '#666'}`,
                    }}
                  />
                ))}
              </div>
            </Select.Option>
          );
        })}
      </Select>
    </div>
  );
};
